(() => {
  const reduced = matchMedia('(prefers-reduced-motion: reduce)');
  const lists = new Set();
  const updates = new Set();
  const revealed = new WeakSet();
  let frame = 0;
  const measure = (list) => {
    if (!list.isConnected) return;
    const active = list.querySelector('[aria-selected="true"], .is-active');
    if (!active || !active.getClientRects().length) {
      list.style.setProperty('--feedback-indicator-width', '0px');
      return;
    }
    const outer = list.getBoundingClientRect();
    const inner = active.getBoundingClientRect();
    list.style.setProperty('--feedback-indicator-x', `${inner.left - outer.left + list.scrollLeft}px`);
    list.style.setProperty('--feedback-indicator-y', `${inner.bottom - outer.top + list.scrollTop - 2}px`);
    list.style.setProperty('--feedback-indicator-width', `${inner.width}px`);
  };
  const schedule = (list) => {
    updates.add(list);
    if (frame) return;
    frame = requestAnimationFrame(() => {
      frame = 0;
      updates.forEach(measure);
      updates.clear();
    });
  };
  const resize = new ResizeObserver((entries) => entries.forEach(({ target }) => schedule(target)));
  const changes = new MutationObserver((entries) => {
    entries.forEach(({ target }) => {
      const list = target.closest?.('[data-feedback-indicator]');
      if (list) schedule(list);
    });
  });
  const entrances = new IntersectionObserver((entries) => {
    entries.forEach(({ target, isIntersecting }) => {
      if (!isIntersecting) return;
      target.dataset.sectionReveal = 'visible';
      revealed.add(target);
      entrances.unobserve(target);
    });
  }, {
    // Wait until the section reaches the lower viewport interior, rather than
    // starting as soon as a sliver crosses the bottom edge. A zero threshold
    // also allows sections taller than the viewport to reveal reliably.
    rootMargin: `0px 0px -${Math.round(innerHeight * 0.15)}px 0px`,
    threshold: 0,
  });
  const initialize = () => {
    if (document.body.dataset.interactionFeedback === 'true') {
      document.querySelectorAll('.hatlab-step, .bulk-order-progress__step').forEach((step) => {
        if (step.querySelector('.feedback-step-check')) return;
        const mark = document.createElement('span');
        mark.className = 'feedback-step-check';
        mark.setAttribute('aria-hidden', 'true');
        mark.innerHTML = '<svg viewBox="0 0 24 24" fill="none"><path d="m5 12 4 4L19 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/></svg>';
        step.append(mark);
      });
    }
    if (document.body.dataset.slidingIndicators === 'true') {
      document.querySelectorAll('.custom-tabs--style-underline [data-tablist]').forEach((list) => {
        if (!lists.has(list)) {
          lists.add(list);
          list.dataset.feedbackIndicator = '';
          resize.observe(list);
          changes.observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected', 'class'] });
        }
        schedule(list);
      });
    }
    if (document.body.dataset.sectionEntrances !== 'true' || reduced.matches) return;
    document.querySelectorAll('main .shopify-section > .section').forEach((section) => {
      if (revealed.has(section) || section.dataset.sectionReveal || section.querySelector('product-card, product-form-component, input, select, textarea, video')) return;
      const bounds = section.getBoundingClientRect();
      if (!bounds.height || bounds.top < innerHeight) return;
      section.dataset.sectionReveal = 'pending';
      entrances.observe(section);
    });
  };
  document.addEventListener('vero:summary-updated', (event) => {
    if (document.body.dataset.interactionFeedback !== 'true' || reduced.matches) return;
    const target = event.target.querySelector('.bulk-order-sidebar__total-value');
    if (!target?.animate) return;
    target.getAnimations().forEach((animation) => animation.cancel());
    target.animate([{ opacity: 0.45 }, { opacity: 1 }], { duration: 360, easing: 'ease-out' });
  });
  document.addEventListener('focusin', (event) => {
    const section = event.target.closest?.('[data-section-reveal="pending"]');
    if (!section) return;
    section.dataset.sectionReveal = 'visible';
    revealed.add(section);
    entrances.unobserve(section);
  });
  document.addEventListener('vero:tabs-ready', initialize);
  document.addEventListener('shopify:section:load', initialize);
  document.addEventListener('shopify:section:unload', (event) => {
    lists.forEach((list) => {
      if (event.target.contains(list)) {
        resize.unobserve(list);
        lists.delete(list);
        updates.delete(list);
      }
    });
    changes.disconnect();
    lists.forEach((list) => changes.observe(list, { subtree: true, childList: true, attributes: true, attributeFilter: ['aria-selected', 'class'] }));
    event.target.querySelectorAll('[data-section-reveal]').forEach((section) => entrances.unobserve(section));
  });
  reduced.addEventListener('change', () => {
    entrances.disconnect();
    document.querySelectorAll('[data-section-reveal="pending"]').forEach((section) => section.removeAttribute('data-section-reveal'));
    initialize();
  });
  document.fonts?.ready.then(() => lists.forEach(schedule));
  requestAnimationFrame(initialize);
})();
